import { Creature } from "./Creature";
import { DropItem } from "./DropItem";
import { Landscape } from "./Landscape";
import { Memory } from "./Memories";
import { Player } from "./Player";
import { Bullet } from "./SlingShot";
import { Character } from "./Character";
import { Portal } from "./Portal";
import { StoryItem } from "./StoryItem";
import { Inventory } from "./UI/Inventory";
import { Structure } from "./Structure";
import { KeyItem } from "./Keys";
import { Hazard } from "./Hazard";
import BaseLevelScene from "src/app/Scenes/BaseLevelScene";
import { Preferences } from "@capacitor/preferences";
import UIScene from "./UI/UIScene";
import { bounceGameObject, showToast } from "./Utilities";
import MainScene from "src/app/Scenes/MainScene";
import { Peer } from "./Peer";
import { MixTape } from "./MixTape";

export function getSortedBodies(pair: Phaser.Types.Physics.Matter.MatterCollisionData): Array<MatterJS.BodyType>{
    var bodyA = pair.bodyA;
    var bodyB = pair.bodyB;
    return [bodyA, bodyB].sort((a, b) => {
        if (a.label === b.label) return 0;
        return a.label > b.label ? 1 : -1;
      });
}

export function getCollisionEventKey(sortedBodies: Array<MatterJS.BodyType>): string{
    return sortedBodies.map((body) => { return body.label}).join('_');
}

function creatureSensorPlayerSensorCallback(creature: Creature, player: Player){
    if(creature.canUpdate){
        if(creature.aggressive){
            creature.chase(player);
            creature.inRange = true;
            creature.initiateAttack();
        }
        else{
            creature.runAway(player);
        }
    }
}

function characterSensorPlayerSensorCallback(character: Character, player: Player){
    character.openDialog();
}

async function playerColliderPortalSensorCallback(player: Player, portal: Portal){
    
    // Refresh mausoleums on exit
    if(portal.scene.scene.key === "Mausoleum 1"){
        const scene = (portal.scene as BaseLevelScene);
        scene.clearCreatureTimers();
        scene.removeAllObjects();
        scene.scene.restart().manager.update(1,1);
        scene.cameras.main.fadeIn(10000, 0, 0, 0);
        setTimeout(() => {
          scene.scene.switch('mainScene');
          scene.scene.get('mainScene').cameras.main.fadeIn(1000, 0, 0, 0);
          BaseLevelScene.skipUpdate = false;
        }, 250);
    }
    else{
        portal.enterOrExit();
    }
}

function playerColliderHouseSensorCallback(house: Structure, player: Player){
    house.enter();
}

async function keySensorPlayerColliderCallback(key: KeyItem, player: Player){
    if(key.pickup()){
        await Inventory.addItem({
            name: "key",
            texture: key.texture.key,
            unlocks: key.unlocks,
        });
    }
}

function gpsSensorPlayerSensorCallback(portal: Portal, player: Player){
    console.log("Player has visited a location");
    portal.updateVisitedPlace();
    if(MainScene.gpsMode.isConnected){
        console.log("Unlocking GPS area");
        portal.unlockGpsEntrance();
    }
    portal.playMusicTrack();
}

function gpsSensorPlayerSensorEndCallback(portal: Portal, player: Player){
    console.log("Player has exited a GPS location");
    portal.stopMusicTrack();
}

function bulletColliderCreatureSensorCallback(bullet: Bullet, creature: Creature){
    if(creature.canUpdate){
        if(creature.scene){
            if(creature.aggressive){
                creature.chase(creature.scene.player);
            }
            else{
                creature.runAway(creature.scene.player);
            }
        }
    }
}

function bulletColliderCreatureColliderCallback(bullet: Bullet, creature: Creature){
    creature.hit();
    bullet.disableBullet();
}

function landscapeColliderPlayerSensorCallback(landscape: Landscape, player: Player){
    if (player.actionPressed) {
        landscape.wiggling = true;
    }
}

async function landscapeColliderPlayerSensorStartCallback(landscape: Landscape, player: Player) {
    
    const HasTouchedLandscapesBefore = await Preferences.get({key: "hasTouchedLandscapes"}).then((result) => {
      return result.value === 'true';
    });

    const settingButtonHasBeenDemoed = await Preferences.get({key: "settingsButtonHasBeenDemoed"}).then((result) => {
        return result.value === 'true';
    });

    if(settingButtonHasBeenDemoed && !HasTouchedLandscapesBefore) {
      console.log("This is the first time you've touched a landscape.");
      await showToast({message: "You encountered a landscape! Try pressing the action button or space bar to whack it."});
      await Preferences.set({key: "hasTouchedLandscapes", value: 'true'});
      // Get the UI scene and flash the action button
      const uiScene = landscape.scene.scene.get('UIScene') as UIScene;
      if (uiScene && uiScene.actionButton) {

        // Bounce the action button so the player sees it.
        bounceGameObject(uiScene.actionButton.button)
      }
    }
  }

async function dropItemColliderPlayerColliderCallback(dropItem: DropItem, player: Player){
    if(dropItem.pickup()){
        if(dropItem.name === 'heart'){
            await Inventory.addItem({
                name: dropItem.name,
                texture: dropItem.texture.key,
                animationKey: 'beat'
            });
        }
        else if(dropItem.name !== "skull" || Inventory.getItemQuantity('skull') === 0){

            await Inventory.addItem({
                name: dropItem.name,
                texture: dropItem.texture.key
            });

            // If this item is a brown_rock then check to see if the slingshot
            // has already been demoed, and if not, demo it.
            if(dropItem.name === 'brown_rock'){
                const settingButtonHasBeenDemoed = await Preferences.get({key: "settingsButtonHasBeenDemoed"}).then((result) => {
                    return result.value === 'true';
                });

                const HasTouchedLandscapesBefore = await Preferences.get({key: "hasTouchedLandscapes"}).then((result) => {
                return result.value === 'true';
                });

                const slingshotHasBeenDemoed = await Preferences.get({key: "slingshotHasBeenDemoed"}).then((result) => {
                return result.value === 'true';
                });

                if(settingButtonHasBeenDemoed && HasTouchedLandscapesBefore && !slingshotHasBeenDemoed) {
                    await Preferences.set({key: "slingshotHasBeenDemoed", value: 'true'});
                    await showToast({message: "You picked up some slingstones!<br>On mobile, press the action button then recoil the joystick and release it like a slingshot.<br>On desktop hold spacebar and press arrow keys to shoot.", duration: 0});
                }
            }
        }
    }
}

async function storyItemSensorPlayerColliderCallback(player: Player, storyItem: StoryItem){
    console.log("storyItemSensor interacted with player!");

    // If the item is able to be picked up, put it in the inventory
    if(storyItem.pickup()){
        await Inventory.addItem({
            name: storyItem.name,
            texture: storyItem.texture.key,
            animationKey: storyItem.animationKey
        });
        if(!MainScene.tokens.includes(storyItem.name)){
            console.log(`adding token: ${storyItem.name}`);
            MainScene.tokens.push(storyItem.name);
            Preferences.set({key: "tokens", value: JSON.stringify(MainScene.tokens)});
            // Store clientModifiedAt in Preferences
            Preferences.set({
            key: 'clientModifiedAt',
            value: new Date().toISOString(),
            });
        }
    }
}

function peerSensorPlayerColliderCallback(peer: Peer, player: Player) {
  if (!player.peersInRange.includes(peer)) {
    player.peersInRange.push(peer);
  }
}

function peerSensorPlayerColliderEndCallback(peer: Peer, player: Player) {
  player.peersInRange = player.peersInRange.filter(p => p !== peer);
}

function characterSensorPlayerSensorEndCallback(character: Character, player: Player){
    character.closeDialog();
}

function playerColliderPortalSensorEndCallback(player: Player, portal: Portal){
    portal.closeDialog();
}

function creatureSensorPlayerSensorEndCallback(creature: Creature, player: Player){
    if(creature.canUpdate){
        creature.inRange = false;
    }
}

function memoryColliderPlayerColliderCallback(memory: Memory, player: Player){
    memory.pickup();
}

function audioColliderPlayerColliderCallback(mixTape: MixTape, player: Player){
    mixTape.pickup();
}

function hazardSensorPlayerColliderCallback(hazard: Hazard, player: Player){
    hazard.applyConsequence(player);
}

function electricArcSensorPlayerColliderCallback(hazard: Hazard, player: Player){ {
        console.log("Handling electric arc collision");
        if (!this.electricArcHurtCooldown) {
            player.hit();
        }
    }
}

function bulletColliderPlayerColliderCallback(bullet: Bullet, player: Player){
    player.hit(bullet.connectionId);
    bullet.disableBullet();
}

function bulletColliderPeerSensorCallback(bullet: Bullet, peer: Peer){
    if(peer.name === bullet.connectionId) return;
    peer.hit();
    bullet.disableBullet();
}

// Note: Interacting objects have been sorted alphabetically.
export const onStartInteractionsMapping = {
    "audioCollider_playerCollider" : audioColliderPlayerColliderCallback,
    "creatureSensor_playerSensor" : creatureSensorPlayerSensorCallback,
    "playerCollider_portalSensor" : playerColliderPortalSensorCallback,
    "bulletCollider_creatureSensor" : bulletColliderCreatureSensorCallback,
    "bulletCollider_creatureCollider" : bulletColliderCreatureColliderCallback,
    "memoryCollider_playerCollider" : memoryColliderPlayerColliderCallback,
    "memoryCollider_playerSensor" : (memory: Memory, player: Player) => { console.log("memoryCollider_playerSensor")},
    "characterSensor_playerSensor" : characterSensorPlayerSensorCallback,
    "gpsSensor_playerSensor" : gpsSensorPlayerSensorCallback,
    "houseSensor_playerCollider" : playerColliderHouseSensorCallback,
    "keySensor_playerCollider": keySensorPlayerColliderCallback,
    "hazardSensor_playerCollider": hazardSensorPlayerColliderCallback,
    "landscapeCollider_playerSensor": landscapeColliderPlayerSensorStartCallback,
    "peerSensor_playerCollider" : peerSensorPlayerColliderCallback,
    "electricArcSensor_playerCollider" : electricArcSensorPlayerColliderCallback,
    "bulletCollider_playerCollider" : bulletColliderPlayerColliderCallback,
    "bulletCollider_peerSensor" : bulletColliderPeerSensorCallback,
}
  

export const onActiveInteractionsMapping = {
    "landscapeCollider_playerSensor": landscapeColliderPlayerSensorCallback,
    "dropItemCollider_playerCollider" : dropItemColliderPlayerColliderCallback,
    "playerCollider_storyItemSensor" : storyItemSensorPlayerColliderCallback,
}

export const onEndInteractionsMapping = {
    "characterSensor_playerSensor_end" : characterSensorPlayerSensorEndCallback,
    "playerCollider_portalSensor_end" : playerColliderPortalSensorEndCallback,
    "creatureSensor_playerSensor_end" : creatureSensorPlayerSensorEndCallback,
    "peerSensor_playerCollider_end" : peerSensorPlayerColliderEndCallback,
    "gpsSensor_playerSensor_end" : gpsSensorPlayerSensorEndCallback,
}